export type RoundPriceOpts = Readonly<{
  locale?: Intl.LocalesArgument;
  currency: Intl.NumberFormatOptions['currency'];
  maximumFractionDigits?: Intl.NumberFormatOptions['maximumFractionDigits'];
}>;

/**
 * Rounds a given price to two decimal places and formats it as a localized currency string.
 *
 * @param price - The numeric price value to be rounded and formatted.
 * @param opts - Optional configuration for localization and currency formatting.
 * @param opts.locale - The locale string used for formatting (default is `en-GB`).
 * @param opts.currency - The currency code used for formatting (default is `GBP`).
 * @returns A string representing the formatted price, with trailing `.00` removed if present.
 */
export function roundPrice(price: number, opts?: RoundPriceOpts): string {
  const { locale, currency, maximumFractionDigits } = opts ?? {};

  // must be a valid locale string, otherwise default to 'en-GB'
  const validLocale = typeof locale === 'string' && locale.trim() ? locale : 'en-GB';

  // must be a valid string, 3 characters long, otherwise default to 'GBP'
  const validCurrency =
    typeof currency === 'string' && currency.trim().length === 3
      ? currency.trim().toUpperCase()
      : 'GBP';

  // maximumFractionDigits must be a number >= 0, otherwise default to 2
  const validFractionDigits =
    typeof maximumFractionDigits === 'number' && maximumFractionDigits >= 0
      ? maximumFractionDigits
      : 2;

  const returnValue = new Intl.NumberFormat(validLocale, {
    style: 'currency',
    currency: validCurrency,
    maximumFractionDigits: validFractionDigits,
  }).format(price);

  if (returnValue.endsWith('.00')) {
    return returnValue.replace('.00', '');
  }

  return returnValue;
}

// https://stackoverflow.com/a/48850944/10933675 - Round half away from zero ('commercial' rounding)
export function round(num: number, decimalPlaces: number): number {
  const p = 10 ** decimalPlaces;
  const e = Number.EPSILON * num * p;
  return Math.round(num * p + e) / p;
}
